import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { 
  FiCopy, 
  FiEdit2, 
  FiTrash2, 
  FiExternalLink, 
  FiEye, 
  FiEyeOff,
  FiLoader,
  FiCalendar,
  FiBarChart
} from 'react-icons/fi';
import { urlService } from '../services/urlService';

const UrlList = ({ urls, loading, onUrlUpdated, onUrlDeleted }) => {
  const [editingId, setEditingId] = useState(null);
  const [editUrl, setEditUrl] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleEdit = (url) => {
    setEditingId(url.id);
    setEditUrl(url.originalUrl);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditUrl('');
  };

  const handleSaveEdit = async (id) => {
    if (!editUrl.trim()) {
      toast.error('URL cannot be empty');
      return;
    }

    try {
      setUpdating(true);
      const updatedUrl = await urlService.updateUrl(id, { originalUrl: editUrl.trim() });
      onUrlUpdated(updatedUrl);
      setEditingId(null);
      setEditUrl('');
      toast.success('URL updated successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to update URL');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleActive = async (url) => {
    try {
      const updatedUrl = await urlService.updateUrl(url.id, { isActive: !url.isActive });
      onUrlUpdated(updatedUrl);
      toast.success(`URL ${updatedUrl.isActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error(error.message || 'Failed to update URL status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this URL?')) {
      return;
    }

    try {
      setDeleting(id);
      await urlService.deleteUrl(id);
      onUrlDeleted(id);
      toast.success('URL deleted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to delete URL');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <FiLoader className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (urls.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">No URLs created yet</p>
        <p className="text-gray-400 text-sm mt-2">Create your first short URL above</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {urls.map((url) => (
        <div key={url.id} className={`border rounded-lg p-6 ${url.isActive ? 'bg-white' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${url.isActive ? 'bg-green-100' : 'bg-gray-200'}`}>
                  {url.isActive ? (
                    <FiEye className="h-4 w-4 text-green-600" />
                  ) : (
                    <FiEyeOff className="h-4 w-4 text-gray-500" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  {editingId === url.id ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter new URL"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(url.id)}
                          disabled={updating}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {updating && <FiLoader className="h-4 w-4 animate-spin" />}
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => copyToClipboard(url.shortUrl)}>
                          {url.shortUrl}
                        </h3>
                        <button
                          onClick={() => copyToClipboard(url.shortUrl)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Copy short URL"
                        >
                          <FiCopy className="h-4 w-4" />
                        </button>
                        <a
                          href={url.originalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Visit original URL"
                        >
                          <FiExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                      
                      <p className="text-gray-600 truncate text-sm" title={url.originalUrl}>
                        {url.originalUrl}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <FiCalendar className="h-4 w-4" />
                          <span>{formatDate(url.createdAt)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiBarChart className="h-4 w-4" />
                          <span>{url.clickCount} clicks</span>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs ${url.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {url.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {editingId !== url.id && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleActive(url)}
                  className={`p-2 rounded-lg ${url.isActive ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
                  title={url.isActive ? 'Deactivate URL' : 'Activate URL'}
                >
                  {url.isActive ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                </button>
                
                <button
                  onClick={() => handleEdit(url)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Edit URL"
                >
                  <FiEdit2 className="h-4 w-4" />
                </button>
                
                <button
                  onClick={() => handleDelete(url.id)}
                  disabled={deleting === url.id}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                  title="Delete URL"
                >
                  {deleting === url.id ? (
                    <FiLoader className="h-4 w-4 animate-spin" />
                  ) : (
                    <FiTrash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default UrlList; 